/**
 * The chat list pane.
 *
 * From docs/references/12-desktop-three-pane.png and 13-android-chat-list.png:
 * title row with a new-chat action, a search field, the filter chips, the
 * Archived row, then the conversations.
 *
 * The chips are not decoration — Unread and Groups carry counts and are how
 * people with hundreds of chats actually navigate. They filter the same
 * subscription rather than refetching, so switching between them is instant.
 */

import { useEffect, useMemo, useState } from 'react'
import { Link, NavLink, useNavigate } from 'react-router-dom'
import { useChats, useAuth } from '../../context/index.js'
import {
  formatListTime, previewText,
  chats as chatService, contacts as contactService
} from '@zivapp/shared'
import Avatar from '../ui/Avatar.jsx'
import './ChatList.css'

const FILTERS = [
  { id: 'all',        label: 'All' },
  { id: 'unread',     label: 'Unread' },
  { id: 'favourites', label: 'Favourites' },
  { id: 'groups',     label: 'Groups' }
]

/** Double tick, shown on your own last message. */
function Ticks ({ read }) {
  return (
    <svg className={`cls__ticks${read ? ' is-read' : ''}`} viewBox="0 0 20 14" fill="none"
      aria-hidden="true">
      <path d="M1 7.6 4.4 11 11.2 3.4" stroke="currentColor" strokeWidth="1.6"
        strokeLinecap="round" strokeLinejoin="round" />
      <path d="M7.6 7.8 10.4 11 17.6 3" stroke="currentColor" strokeWidth="1.6"
        strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

/**
 * The words on a chat-list row.
 *
 * The server sends a ready-made preview, which already carries a voice
 * note's length ("Voice message (0:11)") — mediaMeta never reaches a list
 * row, so it could not be assembled here. previewText() is the fallback
 * for a message held in memory. A deleted newest message is worded by
 * whose it was, as WhatsApp does.
 *
 * Mirrors previewLine() in zivapp-mobile/screens/Chats.jsx.
 */
function previewLine (last, myId) {
  if (!last) return 'No messages yet'
  if (last.deleted) {
    return last.senderId && last.senderId === myId
      ? 'You deleted this message'
      : 'This message was deleted'
  }
  // `||`, not `??`: toChat() coerces a missing preview to '', and '' ?? x
  // is '' — which rendered a blank row instead of falling back.
  return last.preview || previewText(last)
}

/** The glyph before that text. Mirrors PreviewIcon() in the app. */
function PreviewIcon ({ message }) {
  if (!message) return null

  if (message.deleted) {
    return (
      <svg className="cls__ptype" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="8.4" stroke="currentColor" strokeWidth="1.7" />
        <path d="m6.5 6.5 11 11" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      </svg>
    )
  }

  const paths = {
    audio: 'M12 4.2a2.4 2.4 0 0 1 2.4 2.4v4.6a2.4 2.4 0 1 1-4.8 0V6.6A2.4 2.4 0 0 1 12 4.2ZM7.2 11.2v.6a4.8 4.8 0 0 0 9.6 0v-.6M12 16.6V20',
    image: 'M4.5 8h2.3l1.1-1.8h6.2L15.2 8h2.3a1.5 1.5 0 0 1 1.5 1.5v7A1.5 1.5 0 0 1 17.5 18h-11A1.5 1.5 0 0 1 5 16.5v-7A1.5 1.5 0 0 1 6.5 8ZM12 15.2a2.8 2.8 0 1 0 0-5.6 2.8 2.8 0 0 0 0 5.6Z',
    video: 'M4.5 7h10a1.5 1.5 0 0 1 1.5 1.5v7A1.5 1.5 0 0 1 14.5 17h-10A1.5 1.5 0 0 1 3 15.5v-7A1.5 1.5 0 0 1 4.5 7ZM16 11l4-2.2v6.4L16 13',
    document: 'M14 4H8a1.6 1.6 0 0 0-1.6 1.6v12.8A1.6 1.6 0 0 0 8 20h8a1.6 1.6 0 0 0 1.6-1.6V7.6L14 4ZM14 4v4h3.6'
  }

  const d = paths[message.type]
  if (!d) return null

  return (
    <svg className="cls__ptype" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d={d} stroke="currentColor" strokeWidth="1.7"
        strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

export default function ChatList ({ activeId }) {
  const { chats, isLoading } = useChats()
  const { user } = useAuth()
  const navigate = useNavigate()

  const [filter, setFilter] = useState('all')
  const [query, setQuery] = useState('')
  const [contacts, setContacts] = useState([])
  const [opening, setOpening] = useState(null)
  const [menuOpen, setMenuOpen] = useState(false)

  // Needed so a search can offer people you have never messaged.
  useEffect(() => contactService.subscribe(setContacts), [])

  const counts = useMemo(() => ({
    unread: chats.filter(c => c.unreadCount > 0).length,
    groups: chats.filter(c => c.type === 'group').length,
    favourites: chats.filter(c => c.isPinned).length
  }), [chats])

  const visible = useMemo(() => {
    let rows = chats.filter(c => !c.isArchived)

    if (filter === 'unread')     rows = rows.filter(c => c.unreadCount > 0)
    if (filter === 'groups')     rows = rows.filter(c => c.type === 'group')
    if (filter === 'favourites') rows = rows.filter(c => c.isPinned)

    const q = query.trim().toLowerCase()
    if (q) {
      rows = rows.filter(c =>
        (c.title ?? '').toLowerCase().includes(q) ||
        (c.lastMessage?.preview ?? '').toLowerCase().includes(q)
      )
    }
    return rows
  }, [chats, filter, query])

  /**
   * People the search found who you have no conversation with yet.
   *
   * Searching used to filter existing chats and nothing else, so looking
   * for somebody you had never messaged — including YOURSELF, reported
   * as "I searched for obodo and did not see myself" — found nothing at
   * all, which reads as the person not existing.
   *
   * Yourself first, then saved contacts. Tapping either starts the
   * conversation, which is what a search result for a person should do.
   */
  const people = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return []

    /*
     * Deduped by title, not by id: the chat list query does not return
     * participant ids, only the peer's name. It is cosmetic anyway —
     * getOrCreateDirect returns the chat that already exists, so a
     * duplicate row opens the right conversation regardless.
     */
    const already = new Set(
      chats
        .filter(c => c.type === 'direct' && c.title)
        .map(c => c.title.toLowerCase())
    )
    const found = []

    const myName = (user?.displayName ?? '').trim()
    if (myName && myName.toLowerCase().includes(q) && !already.has(myName.toLowerCase())) {
      found.push({
        id: user.id,
        name: `${myName} (You)`,
        hint: 'Message yourself',
        avatarUrl: user.avatarUrl
      })
    }

    for (const contact of contacts) {
      const id = contact.userId ?? contact.user?.id
      const name = contact.savedName || contact.user?.displayName || ''
      if (!id || !name) continue
      if (already.has(name.toLowerCase())) continue
      if (!name.toLowerCase().includes(q)) continue
      found.push({ id, name, hint: 'Start a chat', avatarUrl: contact.user?.avatarUrl })
    }

    return found
  }, [query, chats, contacts, user])

  async function openWith (userId) {
    setOpening(userId)
    try {
      const chat = await chatService.getOrCreateDirect(userId)
      navigate(`/chats/${chat.id}`)
      setQuery('')
    } finally {
      setOpening(null)
    }
  }

  const archivedCount = chats.filter(c => c.isArchived).length

  return (
    <div className="cls">
      <header className="cls__bar">
        <h1 className="cls__brand">ZIvapp</h1>

        {/* Camera, then the overflow menu. New chat is NOT here — on a phone
            it belongs in the floating button at the bottom, within thumb
            reach, which is where the reference puts it. */}
        <div className="cls__bar-actions">
          {/* The camera in this header posts a photo status, which is
              what it does in the app this is built from — not a chat
              attachment, which lives in the composer. */}
          <button
            className="cls__icon-btn"
            type="button"
            aria-label="Add a photo status"
            onClick={() => navigate('/status/new/photo')}
            data-testid="chatlist-camera"
          >
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M4 8h2.8l1.4-2.2h7.6L17.2 8H20a1.8 1.8 0 0 1 1.8 1.8v8.4A1.8 1.8 0 0 1 20 20H4a1.8 1.8 0 0 1-1.8-1.8V9.8A1.8 1.8 0 0 1 4 8Z"
                stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
              <circle cx="12" cy="13.8" r="3.4" stroke="currentColor" strokeWidth="1.8" />
            </svg>
          </button>

          {/* The overflow menu. It had no handler, so it was three dots
              that did nothing — every item here goes somewhere real. */}
          <div className="cls__menu-wrap">
            <button
              className="cls__icon-btn"
              type="button"
              aria-label="Menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen(open => !open)}
              data-testid="chatlist-menu"
            >
              <svg viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="5" r="1.7" fill="currentColor" />
                <circle cx="12" cy="12" r="1.7" fill="currentColor" />
                <circle cx="12" cy="19" r="1.7" fill="currentColor" />
              </svg>
            </button>

            {menuOpen && (
              <>
                {/* A full-screen catcher, so clicking anywhere else
                    closes the menu without every item needing to. */}
                <button
                  className="cls__menu-scrim"
                  type="button"
                  aria-label="Close menu"
                  onClick={() => setMenuOpen(false)}
                />
                <ul className="cls__menu" role="menu">
                  {[
                    ['New group', '/groups/new'],
                    ['New community', '/communities/new'],
                    ['New channel', '/channels/new'],
                    ['Contacts', '/contacts'],
                    ['Profile', '/profile'],
                    ['Settings', '/settings']
                  ].map(([label, to]) => (
                    <li key={to} role="none">
                      <button
                        type="button"
                        role="menuitem"
                        className="cls__menu-item"
                        onClick={() => { setMenuOpen(false); navigate(to) }}
                      >
                        {label}
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>
      </header>

      <div className="cls__search">
        <svg className="cls__search-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.9" />
          <path d="m16.5 16.5 4 4" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
        </svg>
        <input
          className="cls__search-input"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search or start a new chat"
          aria-label="Search chats"
        />
      </div>

      <div className="cls__chips" role="tablist" aria-label="Filter chats">
        {FILTERS.map(f => {
          const count = counts[f.id]
          return (
            <button
              key={f.id}
              type="button"
              role="tab"
              aria-selected={filter === f.id}
              className={`cls__chip${filter === f.id ? ' is-on' : ''}`}
              onClick={() => setFilter(f.id)}
            >
              {f.label}
              {count > 0 && <span className="cls__chip-count">{count}</span>}
            </button>
          )
        })}

        {/* Custom lists. The reference's "+" chip — people with hundreds of
            chats group them by hand, and the four built-in filters cannot
            know what those groups are. No store behind it yet. */}
        <button type="button" className="cls__chip cls__chip--add" aria-label="Create a list">
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" />
          </svg>
        </button>
      </div>

      <div className="cls__scroll">
        {archivedCount > 0 && (
          <Link className="cls__archived" to="/archived">
            <span className="cls__archived-icon">
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M3.5 6.5h17v3h-17zM5 9.5v9h14v-9" stroke="currentColor"
                  strokeWidth="1.8" strokeLinejoin="round" />
                <path d="M10 13h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </span>
            Archived
            <span className="cls__archived-count">{archivedCount}</span>
          </Link>
        )}

        {/* People the search matched who you have no chat with yet —
            yourself included. Above the conversations, because if you
            searched a name and have never messaged them, this is the row
            you were looking for. */}
        {people.length > 0 && (
          <>
            <h2 className="cls__section">Start a new chat</h2>
            <ul className="cls__rows">
              {people.map(person => (
                <li key={person.id}>
                  <button
                    type="button"
                    className="cls__row cls__row--person"
                    onClick={() => openWith(person.id)}
                    disabled={opening === person.id}
                  >
                    <Avatar name={person.name} url={person.avatarUrl} size={49} />
                    <span className="cls__body">
                      <span className="cls__name">{person.name}</span>
                      <span className="cls__preview">
                        {opening === person.id ? 'Opening…' : person.hint}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}

        {isLoading ? (
          <p className="cls__state">Loading chats…</p>
        ) : visible.length === 0 ? (
          people.length === 0 &&
            <EmptyState hasChats={chats.length > 0} query={query} filter={filter} />
        ) : (
          <ul className="cls__rows">
            {visible.map(chat => (
              <li key={chat.id}>
                <NavLink
                  to={`/chats/${chat.id}`}
                  className={`cls__row${chat.id === activeId ? ' is-active' : ''}`}
                >
                  <Avatar name={chat.title} url={chat.avatarUrl ?? chat.peerAvatarUrl} size={49}
                    group={chat.type === 'group'} />

                  <span className="cls__body">
                    <span className="cls__line">
                      <span className="cls__name">{chat.title ?? 'Chat'}</span>
                      <span className={`cls__time${chat.unreadCount > 0 ? ' is-unread' : ''}`}>
                        {formatListTime(chat.lastMessageAt)}
                      </span>
                    </span>

                    <span className="cls__line">
                      <span className="cls__preview">
                        {chat.lastMessage?.senderId === chat.myId && <Ticks read={chat.lastMessage?.readByAll} />}
                        {/* Mic, camera, film, document or tombstone, as
                            WhatsApp shows before the words. */}
                        <PreviewIcon message={chat.lastMessage} />
                        {previewLine(chat.lastMessage, chat.myId)}
                      </span>

                      <span className="cls__meta">
                        {chat.isPinned && (
                          <svg className="cls__pin" viewBox="0 0 24 24" fill="none" aria-label="Pinned">
                            <path d="M9 3h6l-1 6 4 3v2h-5v7l-1 1-1-1v-7H6v-2l4-3Z"
                              stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
                          </svg>
                        )}
                        {chat.unreadCount > 0 && (
                          <span className="cls__badge">
                            {chat.unreadCount > 99 ? '99+' : chat.unreadCount}
                          </span>
                        )}
                      </span>
                    </span>
                  </span>
                </NavLink>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* The new-chat action. Floating over the list, bottom-right, where a
          thumb reaches without stretching to the top of a tall phone. */}
      <Link className="cls__fab" to="/contacts" aria-label="New chat">
        <svg viewBox="0 0 24 24" fill="none">
          <path d="M5 4h14a3 3 0 0 1 3 3v8a3 3 0 0 1-3 3H9l-5 4v-4a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3Z"
            stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
          <path d="M12 8v6M9 11h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </Link>
    </div>
  )
}

/**
 * Three different nothings, which need three different answers: no account
 * activity at all, a filter that matches nothing, and a search that found
 * nothing. Showing "no chats" for all three is how an app feels broken.
 *
 * The first of those used to read "Daniel, Angie, Zack and 8 more of your
 * contacts are on ZIvapp", copied from a reference screenshot. Those were
 * hardcoded names. The reference could say that because it was listing the
 * user's real contacts, read from their phone; ZIvapp does not read contacts
 * and on the web cannot. So it was a screen stating something untrue, which
 * is worse than a plain one.
 *
 * When contact discovery exists — contacts.discover() in the service
 * contract, no provider behind it yet — this can show real names and the
 * stacked avatars come back. Until then it says only what is true.
 */
function EmptyState ({ hasChats, query, filter }) {
  if (query) {
    return <p className="cls__state">No chats match “{query}”.</p>
  }
  if (hasChats) {
    return <p className="cls__state">Nothing in {filter === 'all' ? 'this list' : filter}.</p>
  }
  return (
    <div className="cls__empty">
      <span className="cls__empty-art" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none">
          <path d="M5 4h14a3 3 0 0 1 3 3v8a3 3 0 0 1-3 3H9l-5 4v-4a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3Z"
            stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
          <path d="M8 9h8M8 12.5h5" stroke="currentColor" strokeWidth="1.3"
            strokeLinecap="round" />
        </svg>
      </span>

      <p className="cls__empty-title">No chats yet</p>
      <p className="cls__empty-text">
        Start a conversation and it will show up here.
      </p>

      <Link className="cls__start" to="/contacts">
        Start a chat
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M4 12h15m0 0-5.5-5.5M19 12l-5.5 5.5" stroke="currentColor"
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </Link>
    </div>
  )
}
